import { apiClient } from '../api/apiClient';

export interface DictionaryItem {
  id: number;
  code: string;
  name: string;
  display_name: string;
  description?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

type CacheKey = 
  | 'cachedCapturedVia'
  | 'cachedMaritalStatus'
  | 'cachedActivityLevel'
  | 'cachedMotivationReadiness'
  | 'cachedTechnologyComfortLevel'
  | 'cachedDietType'
  | 'cachedAllergyType'
  | 'cachedFoodIntolerance'
  | 'cachedNutritionalDeficiency'
  | 'cachedWaterIntake'
  | 'cachedSnackFrequency'
  | 'cachedWeekendPattern'
  | 'cachedMealType'
  | 'cachedSleepQuality'
  | 'cachedSleepPattern'
  | 'cachedWorkSchedule'
  | 'cachedStressLevel'
  | 'cachedFamilyStructure'
  | 'cachedCookingResponsibility'
  | 'cachedAlcoholConsumption'
  | 'cachedReligiousRestriction'
  | 'cachedSmokingStatus'
  | 'cachedDiseases'
  | 'cachedMedication'
  | 'cachedPersonalHistory'
  | 'cachedFamilyHistory'
  | 'cachedCultureIdentity';

class NutritionDictionaryService {
  private cachedCapturedVia: DictionaryItem[] | null = null;
  private cachedMaritalStatus: DictionaryItem[] | null = null;
  private cachedActivityLevel: DictionaryItem[] | null = null;
  private cachedMotivationReadiness: DictionaryItem[] | null = null;
  private cachedTechnologyComfortLevel: DictionaryItem[] | null = null;
  private cachedDietType: DictionaryItem[] | null = null;
  private cachedAllergyType: DictionaryItem[] | null = null;
  private cachedFoodIntolerance: DictionaryItem[] | null = null;
  private cachedNutritionalDeficiency: DictionaryItem[] | null = null;
  private cachedWaterIntake: DictionaryItem[] | null = null;
  private cachedSnackFrequency: DictionaryItem[] | null = null;
  private cachedWeekendPattern: DictionaryItem[] | null = null;
  private cachedMealType: DictionaryItem[] | null = null;
  private cachedSleepQuality: DictionaryItem[] | null = null;
  private cachedSleepPattern: DictionaryItem[] | null = null;
  private cachedWorkSchedule: DictionaryItem[] | null = null;
  private cachedStressLevel: DictionaryItem[] | null = null;
  private cachedFamilyStructure: DictionaryItem[] | null = null;
  private cachedCookingResponsibility: DictionaryItem[] | null = null;
  private cachedAlcoholConsumption: DictionaryItem[] | null = null;
  private cachedReligiousRestriction: DictionaryItem[] | null = null;
  private cachedSmokingStatus: DictionaryItem[] | null = null;
  private cachedDiseases: DictionaryItem[] | null = null;
  private cachedMedication: DictionaryItem[] | null = null;
  private cachedPersonalHistory: DictionaryItem[] | null = null;
  private cachedFamilyHistory: DictionaryItem[] | null = null;
  private cachedCultureIdentity: DictionaryItem[] | null = null;

  private fetchPromises: Map<string, Promise<DictionaryItem[]>> = new Map();

  private async fetchDictionary(endpoint: string, cacheKey: CacheKey, retryCount = 0): Promise<DictionaryItem[]> {
    const maxRetries = 1;

    // Check cache first
    const cached = this[cacheKey] as DictionaryItem[] | null;
    if (cached) {
      return cached;
    }

    // Check if there's already a fetch in progress
    const existingPromise = this.fetchPromises.get(endpoint);
    if (existingPromise) {
      return existingPromise;
    }

    const doFetch = (): Promise<DictionaryItem[]> =>
      apiClient
      .get<DictionaryItem[]>(`/api/v1/${endpoint}`)
      .then((response) => {
        const items = (response.data || []).filter((item) => item.is_active);
        this[cacheKey] = items;
        this.fetchPromises.delete(endpoint);
        return items;
      })
        .catch(async (error) => {
          const isRetryable =
            error?.code === 'ERR_NETWORK' ||
            error?.code === 'ECONNABORTED' ||
            (typeof error?.message === 'string' && error.message.includes('Network Error'));
          if (isRetryable && retryCount < maxRetries) {
            this.fetchPromises.delete(endpoint);
            await new Promise((r) => setTimeout(r, 400));
            return this.fetchDictionary(endpoint, cacheKey, retryCount + 1);
          }
          if (retryCount >= maxRetries) {
            console.warn(`Dictionary ${endpoint} unavailable after retry. Using empty list.`);
          } else {
            console.warn(`Failed to fetch ${endpoint}:`, error?.message || error);
          }
        this.fetchPromises.delete(endpoint);
        return [];
      });

    const promise = doFetch();
    this.fetchPromises.set(endpoint, promise);
    return promise;
  }

  async getCapturedVia(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('captured-via/', 'cachedCapturedVia');
  }

  async getMaritalStatus(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('marital-status/', 'cachedMaritalStatus');
  }

  async getActivityLevel(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('activity-level/', 'cachedActivityLevel');
  }

  async getMotivationReadiness(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('motivation-readiness/', 'cachedMotivationReadiness');
  }

  async getTechnologyComfortLevel(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('technology-comfort-level/', 'cachedTechnologyComfortLevel');
  }

  async getDietType(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('diet-type/', 'cachedDietType');
  }

  async getAllergyType(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('allergy-type/', 'cachedAllergyType');
  }

  async getFoodIntolerance(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('food-intolerance/', 'cachedFoodIntolerance');
  }

  async getNutritionalDeficiency(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('nutritional-deficiency/', 'cachedNutritionalDeficiency');
  }

  async getWaterIntake(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('water-intake/', 'cachedWaterIntake');
  }

  async getSnackFrequency(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('snack-frequency/', 'cachedSnackFrequency');
  }

  async getWeekendPattern(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('weekend-pattern/', 'cachedWeekendPattern');
  }

  async getMealType(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('meal-type/', 'cachedMealType');
  }

  async getSleepQuality(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('sleep-quality/', 'cachedSleepQuality');
  }

  async getSleepPattern(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('sleep-pattern/', 'cachedSleepPattern');
  }

  async getWorkSchedule(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('work-schedule/', 'cachedWorkSchedule');
  }

  async getStressLevel(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('stress-level/', 'cachedStressLevel');
  }

  async getFamilyStructure(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('family-structure/', 'cachedFamilyStructure');
  }

  async getCookingResponsibility(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('cooking-responsibility/', 'cachedCookingResponsibility');
  }

  async getAlcoholConsumption(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('alcohol-consumption/', 'cachedAlcoholConsumption');
  }

  async getReligiousRestriction(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('religious-restriction/', 'cachedReligiousRestriction');
  }

  async getSmokingStatus(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('smoking-status/', 'cachedSmokingStatus');
  }

  async getDiseases(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('diseases/', 'cachedDiseases');
  }

  async getMedication(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('medication/', 'cachedMedication');
  }

  async getPersonalHistory(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('personal-history/', 'cachedPersonalHistory');
  }

  async getFamilyHistory(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('family-history/', 'cachedFamilyHistory');
  }

  async getCultureIdentity(): Promise<DictionaryItem[]> {
    return this.fetchDictionary('culture-identity/', 'cachedCultureIdentity');
  }

  /**
   * Map of dictionary set key (used in UI state) to getter. Used for step-based lazy loading.
   */
  private readonly keyToGetter: Record<
    string,
    () => Promise<DictionaryItem[]>
  > = {
    capturedVia: () => this.getCapturedVia(),
    maritalStatus: () => this.getMaritalStatus(),
    activityLevel: () => this.getActivityLevel(),
    motivationReadiness: () => this.getMotivationReadiness(),
    technologyComfortLevel: () => this.getTechnologyComfortLevel(),
    dietType: () => this.getDietType(),
    allergyType: () => this.getAllergyType(),
    foodIntolerance: () => this.getFoodIntolerance(),
    nutritionalDeficiency: () => this.getNutritionalDeficiency(),
    waterIntake: () => this.getWaterIntake(),
    snackFrequency: () => this.getSnackFrequency(),
    weekendPattern: () => this.getWeekendPattern(),
    mealType: () => this.getMealType(),
    sleepQuality: () => this.getSleepQuality(),
    sleepPattern: () => this.getSleepPattern(),
    workSchedule: () => this.getWorkSchedule(),
    stressLevel: () => this.getStressLevel(),
    familyStructure: () => this.getFamilyStructure(),
    cookingResponsibility: () => this.getCookingResponsibility(),
    alcoholConsumption: () => this.getAlcoholConsumption(),
    religiousRestriction: () => this.getReligiousRestriction(),
    smokingStatus: () => this.getSmokingStatus(),
    diseases: () => this.getDiseases(),
    medication: () => this.getMedication(),
    personalHistory: () => this.getPersonalHistory(),
    familyHistory: () => this.getFamilyHistory(),
    cultureIdentity: () => this.getCultureIdentity(),
  };

  /**
   * Load only the requested dictionary keys (e.g. for one step). Returns a partial
   * dictionary set to merge into UI state. Uses cache; safe to call per step.
   */
  async loadDictionariesForKeys(
    keys: string[]
  ): Promise<Partial<Record<string, DictionaryItem[]>>> {
    if (keys.length === 0) return {};
    const uniq = Array.from(new Set(keys)).filter((k) => this.keyToGetter[k]);
    const getters = uniq.map((k) => this.keyToGetter[k]!());
    const results = await Promise.all(getters);
    const out: Partial<Record<string, DictionaryItem[]>> = {};
    uniq.forEach((key, i) => {
      out[key] = results[i] ?? [];
    });
    return out;
  }

  /**
   * Load all dictionaries in parallel (for callers that need everything at once)
   */
  async loadAllDictionaries(): Promise<void> {
    await Promise.all([
      this.getCapturedVia(),
      this.getMaritalStatus(),
      this.getActivityLevel(),
      this.getMotivationReadiness(),
      this.getTechnologyComfortLevel(),
      this.getDietType(),
      this.getAllergyType(),
      this.getFoodIntolerance(),
      this.getNutritionalDeficiency(),
      this.getWaterIntake(),
      this.getSnackFrequency(),
      this.getWeekendPattern(),
      this.getMealType(),
      this.getSleepQuality(),
      this.getSleepPattern(),
      this.getWorkSchedule(),
      this.getStressLevel(),
      this.getFamilyStructure(),
      this.getCookingResponsibility(),
      this.getAlcoholConsumption(),
      this.getReligiousRestriction(),
      this.getSmokingStatus(),
      this.getDiseases(),
      this.getMedication(),
      this.getPersonalHistory(),
      this.getFamilyHistory(),
      this.getCultureIdentity(),
    ]);
  }
}

export const nutritionDictionaryService = new NutritionDictionaryService();
